import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Loader2, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-card-buy`;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const usePoll = (token, buyId) => {
  const [p, setP] = useState(null);
  useEffect(() => {
    if (!buyId) return undefined;
    let stop = false;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const r = await axios.post(`${API}/${buyId}/confirm`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setP(r.data);
        if (r.data.status === 'processing' && !stop && tries < 40) setTimeout(tick, 6000);
      } catch (e) { setP({ status: 'error', error: e.response?.data?.detail || 'Could not verify payment' }); }
    };
    tick();
    return () => { stop = true; };
  }, [token, buyId]);
  return p;
};

export const CardBuyPanel = ({ token }) => {
  const params = new URLSearchParams(window.location.search);
  const buyId = params.get('card_buy');
  const [amount, setAmount] = useState(params.get('amount') || '100');
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const status = usePoll(token, buyId);
  useEffect(() => {
    setQ(null);
    if (!(Number(amount) >= 5)) return undefined;
    const t = setTimeout(() => axios.get(`${API}/quote`, { params: { usd_amount: amount } }).then(r => setQ(r.data)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [amount]);
  const pay = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/checkout`, { usd_amount: Number(amount) }, { headers: { Authorization: `Bearer ${token}` } }); window.location.href = r.data.checkout_url; }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Checkout failed'); setBusy(false); }
  };
  return (
    <div className="max-w-lg bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4" data-testid="card-buy-panel">
      <p className="text-white font-bold flex items-center gap-2"><CreditCard className="w-5 h-5 text-emerald-400" /> Buy BETH USD with a card</p>
      {params.get('card_buy_cancelled') && <p className="text-xs text-amber-300" data-testid="card-buy-cancelled">Checkout cancelled. No charge was made.</p>}
      {status && (
        <div className="text-sm rounded-lg p-3 bg-slate-900 border border-slate-700" data-testid="card-buy-status">
          {status.status === 'processing' && <p className="text-amber-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Payment received. Delivering {money(status.beth_amount).slice(1)} BETH USD to your wallet…</p>}
          {status.status === 'completed' && <p className="text-emerald-300">Delivered {money(status.beth_amount).slice(1)} BETH USD to your wallet. {status.explorer_url && <a href={status.explorer_url} target="_blank" rel="noreferrer" className="underline">View <ExternalLink className="w-3 h-3 inline" /></a>}</p>}
          {status.status === 'needs_review' && <p className="text-red-300">Payment received; delivery needs a manual check. Our team has been notified.</p>}
          {status.status === 'error' && <p className="text-red-300">{status.error}</p>}
        </div>
      )}
      <div>
        <label className="text-xs text-slate-400">Amount of BETH USD ($5 – $5,000)</label>
        <input type="number" min="5" max="5000" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full mt-1 px-3 py-2 bg-slate-950 border border-slate-600 rounded text-white text-lg font-mono" data-testid="card-buy-amount" />
      </div>
      {q && <p className="text-sm text-slate-300" data-testid="card-buy-quote">{money(q.beth_amount).slice(1)} BETH USD + {money(q.fee_usd)} card fee (3%) = <b className="text-white">{money(q.total_usd)}</b></p>}
      {!q && amount !== '' && !(Number(amount) >= 5 && Number(amount) <= 5000) && <p className="text-xs text-amber-300" data-testid="card-buy-range">Enter an amount between $5 and $5,000.</p>}
      <button onClick={pay} disabled={busy || !q} className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg font-semibold" data-testid="card-buy-submit">{busy ? 'Opening secure checkout…' : `Pay ${q ? money(q.total_usd) : ''} by card`}</button>
      <p className="text-[11px] text-slate-500">Secure checkout by Stripe. Your payment is added to the cash reserves and the BETH USD is created 1:1 straight into your BETH wallet on Base. Not FDIC insured.</p>
    </div>
  );
};
